import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { es, fr, enUS } from "date-fns/locale";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocaleProvider } from "../../../../components/locale-provider";
import { messages } from "./messages";
import { EXTRA_TYPE, DATE_SELECTION_TYPE } from "../../../witadmin/Common/constants";
import {
  ExtraSelectionEntity,
  Extra,
  ExtraWithFlags,
  InventoryConfiguration,
  ExtraType,
  DateSelectionType,
} from "./domain/extraSelection.entity";

type MessageKey = keyof typeof messages;

interface ExtraSelectionProps {
  catalogExtras: ExtraWithFlags[];
  checkIn: string;
  checkOut: string;
  onConfirm: (selectedExtras: Extra[]) => void;
  onCancel: () => void;
  selectedExtras: ExtraWithFlags[];
  originalExtras: Extra[];
  originalName: string;
  currentInventoryTicker: string;
  scrollToExtraTicker?: string | null;
  currentInventory?: InventoryConfiguration;
}

const QUANTITY_OPTIONS = Array.from({ length: 11 }, (_, index) => index);

export default function ExtrasSelection({
  catalogExtras,
  checkIn,
  checkOut,
  onConfirm,
  onCancel,
  selectedExtras,
  originalExtras,
  originalName,
  scrollToExtraTicker,
  currentInventory,
}: ExtraSelectionProps) {
  const [selected, setSelected] = useState<ExtraWithFlags[]>(
    () => selectedExtras as ExtraWithFlags[]
  );
  const extraRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const intl = useIntl();
  const localeProvider = useLocaleProvider();

  const extraSelectionEntity = useMemo(
    () => new ExtraSelectionEntity(checkIn, checkOut, catalogExtras, currentInventory),
    [checkIn, checkOut, catalogExtras, currentInventory]
  );

  const stayDates = useMemo(() => extraSelectionEntity.getStayDates(), [extraSelectionEntity]);

  const getDateFnsLocale = useCallback(() => {
    const locale = localeProvider.currentLocale;
    if (locale.startsWith("fr")) {
      return fr;
    }
    if (locale.startsWith("en")) {
      return enUS;
    }
    return es;
  }, [localeProvider.currentLocale]);

  useEffect(() => {
    if (scrollToExtraTicker && extraRefs.current[scrollToExtraTicker]) {
      extraRefs.current[scrollToExtraTicker]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [scrollToExtraTicker]);

  const mergedExtras = useMemo(() => extraSelectionEntity.mergeExtras(selected), [
    extraSelectionEntity,
    selected,
  ]);

  const selectedTickersMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    selected.forEach(selectedExtra => {
      map[selectedExtra.ticker] = true;
    });
    return map;
  }, [selected]);

  const isChecked = useCallback((ticker: string) => selectedTickersMap[ticker], [
    selectedTickersMap,
  ]);

  const update = useCallback((ticker: string, patch: Partial<Extra>) => {
    setSelected(prev => {
      const idx = prev.findIndex(extra => extra.ticker === ticker);
      if (idx === -1) {
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  const getWarnings = useCallback(
    (extra: ExtraWithFlags): string[] => {
      const warnings: string[] = [];
      if (!extra.isAppliedToCurrentInventoryTicker) {
        warnings.push(
          intl.formatMessage(messages.extraNotAppliedToInventory, { name: extra.name })
        );
      }
      if (!extra.isAvailable) {
        warnings.push(intl.formatMessage(messages.extraNotAvailable, { name: extra.name }));
      }
      if (extra.isAvailable && extra.isRestricted) {
        warnings.push(intl.formatMessage(messages.extraRestricted, { name: extra.name }));
      }
      return warnings;
    },
    [intl]
  );

  const toggleExtra = useCallback(
    (extra: ExtraWithFlags) => {
      setSelected(prev => {
        const exists = prev.some(item => item.ticker === extra.ticker);
        if (exists) {
          return prev.filter(item => item.ticker !== extra.ticker);
        }

        return [...prev, extraSelectionEntity.initializeExtra(extra)];
      });
    },
    [extraSelectionEntity]
  );

  const handleUnitQuantity = useCallback(
    (extra: ExtraWithFlags, quantity: number) => {
      const isCurrentlySelected = isChecked(extra.ticker);

      if (!isCurrentlySelected && quantity === 0) {
        return;
      }

      const workingExtra = isCurrentlySelected
        ? extra
        : extraSelectionEntity.initializeExtra(extra);
      const result = extraSelectionEntity.updateUnitQuantity(quantity, workingExtra);

      if (result === null) {
        setSelected(prev => prev.filter(item => item.ticker !== extra.ticker));
        return;
      }

      if (!isCurrentlySelected) {
        setSelected(prev => [...prev, result]);
      } else {
        update(extra.ticker, result);
      }
    },
    [isChecked, update, extraSelectionEntity]
  );

  const handleDateValue = useCallback(
    (extra: ExtraWithFlags, date: string, value: number) => {
      const isCurrentlySelected = isChecked(extra.ticker);

      if (!isCurrentlySelected && value === 0) {
        return;
      }

      const workingExtra = isCurrentlySelected
        ? extra
        : extraSelectionEntity.initializeExtra(extra, date, value);
      const result = extraSelectionEntity.updateDateValue(date, value, workingExtra);

      if (result === null) {
        setSelected(prev => prev.filter(item => item.ticker !== extra.ticker));
        return;
      }

      if (!isCurrentlySelected) {
        setSelected(prev => [...prev, result]);
        return;
      }

      update(extra.ticker, result);
    },
    [isChecked, update, extraSelectionEntity]
  );

  const getDisplayPrice = useCallback(
    (extra: ExtraWithFlags, selectedExtra: ExtraWithFlags | undefined): string => {
      return extraSelectionEntity.getDisplayPrice(extra, selectedExtra).toFixed(2);
    },
    [extraSelectionEntity]
  );

  const unitLabel = useCallback(
    (extra: Extra) => {
      const labelMapping: Record<ExtraType, Record<DateSelectionType, MessageKey>> = {
        UNIT: {
          NONE: "unitLabel",
          ALL: "unitDayLabel",
          SPECIFIC: "unitDateLabel",
        },
        ROOM: {
          NONE: "roomLabel",
          ALL: "roomDayLabel",
          SPECIFIC: "roomDateLabel",
        },
        PERSON: {
          NONE: "personLabel",
          ALL: "personDayLabel",
          SPECIFIC: "personDateLabel",
        },
      };

      return intl.formatMessage(messages[labelMapping[extra.type][extra.dateSelectionType]]);
    },
    [intl]
  );

  const getSelectValue = useCallback(
    (extra: ExtraWithFlags, selectedExtra: ExtraWithFlags | undefined): number => {
      return extraSelectionEntity.getSelectValue(extra, selectedExtra);
    },
    [extraSelectionEntity]
  );

  const calculateFinalQuantity = useCallback(
    (extra: ExtraWithFlags, selectedUnits: number): number => {
      return extraSelectionEntity.calculateFinalQuantity(extra, selectedUnits);
    },
    [extraSelectionEntity]
  );

  const getDateLabelMessage = useCallback((extra: ExtraWithFlags) => {
    return extra.type === EXTRA_TYPE.UNIT ? messages.unitsPerDay : messages.daysOfStay;
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(selected.map(extra => extraSelectionEntity.stripFlags(extra)));
  }, [onConfirm, selected, extraSelectionEntity]);

  const selectClassName =
    "border border-gray-300 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400";

  return (
    <div className="text-[#2E3862]">
      <div>
        {originalExtras && originalName && (
          <div className="pt-2 flex flex-column gap-4 ">
            <div className="text-[18px] font-semibold ">
              <FormattedMessage {...messages.originalExtras} />
            </div>
            <div className="text-[13px] font-semibold">{originalName}</div>
            {originalExtras.map((extra: Extra) => (
              <div key={extra.ticker} className="text-[12px]">
                {extra.quantity} x {extra.name}
              </div>
            ))}
          </div>
        )}
        {mergedExtras.map((extra, index) => {
          const warnings = getWarnings(extra);
          const isSelected = isChecked(extra.ticker);

          return (
            <div
              key={extra.ticker}
              className={index === 0 ? "pt-2" : "pt-6"}
              ref={el => (extraRefs.current[extra.ticker] = el)}>
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start">
                  <div className="flex-1 pr-4">
                    <h3 className="text-[18px] font-semibold">{extra.name}</h3>

                    {warnings.length > 0 && (
                      <div className="mt-2 mb-2">
                        {warnings.map((msg, idx) => (
                          <div key={idx} className="text-orange-600 text-sm">
                            {msg}
                          </div>
                        ))}
                      </div>
                    )}

                    {extra.dateSelectionType === DATE_SELECTION_TYPE.SPECIFIC && (
                      <>
                        <div className="mt-3 text-sm font-semibold">
                          <FormattedMessage {...getDateLabelMessage(extra)} />
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-3">
                          {stayDates.map(date => (
                            <div key={date} className="flex items-center gap-3">
                              <span className="text-sm">
                                {format(new Date(date), "eee, dd MMM", {
                                  locale: getDateFnsLocale(),
                                }).replace(/\./g, "")}
                              </span>

                              {extra.type === EXTRA_TYPE.UNIT ? (
                                <select
                                  className={selectClassName}
                                  value={extra.dateQuantity?.[date] ?? 0}
                                  onChange={event =>
                                    handleDateValue(extra, date, Number(event.target.value))
                                  }>
                                  {QUANTITY_OPTIONS.map(optionValue => (
                                    <option key={optionValue} value={optionValue}>
                                      {optionValue}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 focus:ring-blue-400 !mt-0"
                                  checked={!!extra.dateQuantity?.[date]}
                                  onChange={event =>
                                    handleDateValue(extra, date, event.target.checked ? 1 : 0)
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-end">
                    <div className="text-right">
                      <span>{extra.amount.currency}</span>{" "}
                      <span className="font-semibold">
                        {getDisplayPrice(extra, isSelected ? extra : undefined)}
                      </span>
                      <div className="text-xs mt-1">{unitLabel(extra)}</div>
                    </div>

                    <div className="mt-3">
                      {extra.type === EXTRA_TYPE.UNIT &&
                      extra.dateSelectionType !== DATE_SELECTION_TYPE.SPECIFIC ? (
                        <select
                          className={selectClassName}
                          value={getSelectValue(extra, isSelected ? extra : undefined)}
                          onChange={event => {
                            const selectedUnits = Number(event.target.value);
                            const finalQuantity = calculateFinalQuantity(extra, selectedUnits);
                            handleUnitQuantity(extra, finalQuantity);
                          }}>
                          {QUANTITY_OPTIONS.map(optionValue => (
                            <option key={optionValue} value={optionValue}>
                              {optionValue}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="checkbox"
                          className="w-5 h-5 text-blue-600 focus:ring-blue-400 !mt-0"
                          checked={isChecked(extra.ticker)}
                          onChange={() => toggleExtra(extra)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-[-16px] flex justify-content-center gap-4 border-t border-gray-200 px-6 py-4 bg-white">
        <div onClick={handleConfirm} className="btn btn-primary cursor-pointer">
          <FormattedMessage {...messages.confirm} />
        </div>
        <div onClick={onCancel} className="btn btn-secondary cursor-pointer">
          <FormattedMessage {...messages.cancel} />
        </div>
      </div>
    </div>
  );
}
